// Maison de Cocon -- market suggestion popup (native replacement for the Selecty app's
// geolocation popup). Loaded as a deferred module script only when
// sections/custom-market-suggestion-popup.liquid is enabled.
//
// The store is on the Shopify Basic plan, so `localization.country` is never auto-populated
// from the visitor's IP (that pre-selection is a Plus-only feature). Real country detection
// therefore happens here, client-side, via Shopify's own first-party
// /browsing_context_suggestions.json endpoint (detected_values.country). It replaced a call to
// Cloudflare's public trace endpoint (2026-10-11); the surrounding flow and all timings are unchanged.
//
// QA: append ?mdc_geo=XX to any URL to simulate a detected country XX (no network lookup) and to
// print every gate decision to the console ("[mdc-geo]"). Without the parameter nothing is logged.
//
// A detected country outside supportedCountries (our real markets) branches to
// showUnsupportedPopup() instead of being silently dropped -- see that function below.

(function () {
  const root = document.getElementById('market-suggestion-popup');
  if (!root) return;

  const DISMISS_KEY = 'mdc:market-suggestion-popup-resolved';

  // QA / debug -- completely inert unless the URL has ?mdc_geo=... (any value turns logging on; a
  // two-letter value additionally overrides the detected country). Logging is synchronous
  // console output only: it never delays, reorders or gates anything below.
  const QA_PARAM = (function () {
    try {
      return new URLSearchParams(window.location.search).get('mdc_geo');
    } catch (e) {
      return null;
    }
  })();
  const DEBUG = QA_PARAM !== null;
  const QA_COUNTRY = DEBUG && /^[A-Za-z]{2}$/.test(QA_PARAM) ? QA_PARAM.toUpperCase() : null;

  function log() {
    if (!DEBUG) return;
    console.info.apply(console, ['[mdc-geo]', Math.round(performance.now()) + 'ms'].concat([].slice.call(arguments)));
  }
  const activeCountry = root.dataset.activeCountry;
  const supportedCountries = (root.dataset.supportedCountries || '').split(',').filter(Boolean);

  // Keeps the form's hidden locale_code field in sync with the selected country's default
  // locale (data-default-locale, set in snippets/custom-market-suggestion-popup.liquid) so that
  // submitting a country also carries the visitor into that market's language -- mirrors
  // snippets/custom-country-language-selector.liquid's 'country' type inline script.
  function syncLocaleFallback(select) {
    const localeFallbackInput = root.querySelector('[data-market-popup-locale-fallback]');
    if (!localeFallbackInput) return;

    const option = select.selectedOptions && select.selectedOptions[0];
    if (option && option.dataset.defaultLocale) {
      localeFallbackInput.value = option.dataset.defaultLocale;
    }
  }

  function isResolvedThisSession() {
    try {
      return sessionStorage.getItem(DISMISS_KEY) === 'true';
    } catch (e) {
      return false; // storage blocked (e.g. Safari private mode) -- treat as not resolved
    }
  }

  function markResolved() {
    try {
      sessionStorage.setItem(DISMISS_KEY, 'true');
    } catch (e) {
      // storage blocked -- fail silently, worst case the popup can reappear next load
    }
  }

  // Delays the popup's reveal until LCP has settled (or a hard ceiling elapses), so this
  // popup never becomes the page's LCP element itself -- confirmed live via PSI mobile
  // (2026-09-12) that its full-screen overlay can occlude the hero content and get picked
  // as a new, late LCP candidate otherwise (LCP 3.8s -> 9.9s regression). Detection
  // (detectVisitorCountry, below) still starts immediately and is completely unaffected --
  // only the final root.hidden = false reveal in init() waits on this too.
  //
  // Primary signal: PerformanceObserver on 'largest-contentful-paint'. The browser can
  // report a NEW (larger) candidate multiple times as the page loads, so a single entry
  // isn't necessarily final -- each new entry restarts a short 500ms "quiet period" timer,
  // and we resolve once 500ms passes with no further candidate. ceilingMs is a hard cap for
  // browsers without LCP support (old Safari) or the rare case no entry ever fires at all --
  // tradeoff: on very slow connections this can reveal the popup before the true LCP if
  // detection also happens to finish first, but only past the ceiling, and only when the
  // primary signal is unavailable/silent -- never on a normal Chrome/Edge/modern-browser load.
  function whenLCPSettled(ceilingMs) {
    return new Promise((resolve) => {
      let settled = false;
      let quietTimer;
      let ceilingTimer;
      let observer;

      function finish() {
        if (settled) return;
        settled = true;
        clearTimeout(quietTimer);
        clearTimeout(ceilingTimer);
        if (observer) {
          try {
            observer.disconnect();
          } catch (e) {
            // ignore
          }
        }
        resolve();
      }

      const lcpSupported =
        'PerformanceObserver' in window &&
        PerformanceObserver.supportedEntryTypes &&
        PerformanceObserver.supportedEntryTypes.includes('largest-contentful-paint');

      if (lcpSupported) {
        try {
          observer = new PerformanceObserver(() => {
            clearTimeout(quietTimer);
            quietTimer = setTimeout(finish, 500);
          });
          observer.observe({ type: 'largest-contentful-paint', buffered: true });
        } catch (e) {
          // fall through to the ceiling-only timer below
        }
      }

      ceilingTimer = setTimeout(finish, ceilingMs);
    });
  }

  // Started immediately at script load (not inside init()) so it measures the page's real
  // LCP timing, independent of how long the consent-gating flow below takes to resolve.
  //
  // ceilingMs = 6500, raised from an initial 3000 after live-retesting on PSI mobile still
  // showed LCP 9.4s with the popup's flag icon as the LCP element at 3000ms. Root cause is
  // deeper than the ceiling value: Chrome's LCP algorithm can demote/exclude an already-
  // painted element once something else fully occludes it, then falls back to the largest
  // still-visible candidate -- this popup's full-viewport overlay does exactly that to the
  // hero image, regardless of how long the reveal is delayed, as long as it appears before
  // LCP tracking finalizes. For a REAL visitor this is a non-issue: LCP finalizes at their
  // first tap/scroll/keypress, which on mobile overwhelmingly happens within a couple of
  // seconds -- almost always before this popup could possibly appear. The problem is
  // specific to Lighthouse/PSI's synthetic audit, which never interacts with the page, so
  // LCP tracking keeps running for as long as its own trace collection continues (observed
  // past 9s in this store's runs) -- no finite ceiling here can fully guarantee avoiding
  // that lab-only scenario without either delaying past Lighthouse's own trace window
  // (impractically long for a real feature) or redesigning the overlay to not fully occlude
  // the hero. 6500ms is a reasonable, evidence-based middle ground: comfortably past this
  // page's actual measured LCP (~3.8s clean), so real users never notice a meaningfully
  // slower popup, without pretending a single number here can also satisfy a no-interaction
  // synthetic crawl. If PSI mobile still flags this after 6500ms, that's the expected,
  // understood residual gap -- flagged to the team rather than chased further via ceiling
  // tuning alone.
  const lcpSettled = whenLCPSettled(6500);
  if (DEBUG) {
    // Separate observer branch: lcpSettled itself is untouched.
    lcpSettled.then(() => log('lcp: settled -- popup may reveal from now on'));
    log('boot: active country', activeCountry, '| supported', supportedCountries.join(','), '| QA override', QA_COUNTRY || 'none');
  }

  // Isolated on purpose: if this endpoint ever changes format or availability, this is the one
  // function to fix. Fails silently (resolves null) on any error, block, timeout or unexpected
  // response shape -- the popup simply never shows rather than surfacing anything to the visitor.
  //
  // First-party since 2026-10-11 (Shopify's own browsing-context suggestions, IP-based, same origin
  // -- no third-party request). Same 2500ms abort timeout as the Cloudflare call it replaced.
  function detectVisitorCountry() {
    if (QA_COUNTRY) {
      log('detect: QA override ->', QA_COUNTRY, '(no network request)');
      return Promise.resolve(QA_COUNTRY);
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 2500);
    const routesRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    return fetch(routesRoot + 'browsing_context_suggestions.json?country%5Benabled%5D=true&language%5Benabled%5D=false', {
      signal: controller.signal,
      headers: { Accept: 'application/json' },
    })
      .then((response) => (response.ok ? response.json() : Promise.reject(new Error('HTTP ' + response.status))))
      .then((data) => {
        const country = data && data.detected_values && data.detected_values.country;
        const handle = country && country.handle;
        if (typeof handle === 'string' && /^[A-Z]{2}$/.test(handle)) {
          log('detect: endpoint returned', handle);
          return handle;
        }
        log('detect: unexpected response shape -> doing nothing');
        return null;
      })
      .catch((error) => {
        log('detect: lookup failed ->', error && error.message ? error.message : error);
        return null;
      })
      .finally(() => clearTimeout(timeoutId));
  }

  function showPopup(detectedCountry) {
    const option = root.querySelector(`option[value="${detectedCountry}"]`);
    if (!option) {
      log('stop: no <option> for detected country', detectedCountry);
      return;
    }

    root.querySelector('[data-market-popup-flag]').className = `country-flags country-flags--${detectedCountry}`;
    root.querySelector('[data-market-popup-text]').textContent = root
      .querySelector('[data-market-popup-text]')
      .textContent.replace('%COUNTRY%', option.textContent);

    const select = root.querySelector('select[name="country_code"]');
    if (select) {
      select.value = detectedCountry;
      syncLocaleFallback(select); // setting .value programmatically doesn't fire 'change'
    }

    root.hidden = false;
    requestAnimationFrame(() => root.classList.add('is-visible'));
    log('show: supported-country popup revealed for', detectedCountry);
  }

  // Best-effort localized country name for a code outside our own supported_country_codes list
  // (so there's no matching <option> to read the name from, unlike showPopup() above). Falls
  // back to the raw ISO code on engines without Intl.DisplayNames (very old browsers only).
  function getCountryDisplayName(countryCode) {
    try {
      return new Intl.DisplayNames([document.documentElement.lang], { type: 'region' }).of(countryCode);
    } catch (e) {
      return countryCode;
    }
  }

  // Same panel/select/CTA/redirect as showPopup() -- only the heading/body copy differs (see
  // data-unsupported-text attributes in the liquid). The select still only lists our real
  // supported_country_codes and keeps whatever the page already pre-selected (the visitor's
  // current active market), since there's no sensible "detected" option to preselect here.
  function showUnsupportedPopup(detectedCountry) {
    const heading = root.querySelector('[data-market-popup-heading]');
    const text = root.querySelector('[data-market-popup-text]');
    const countryName = getCountryDisplayName(detectedCountry);

    heading.textContent = heading.dataset.unsupportedText.replace('%COUNTRY%', countryName);
    text.textContent = text.dataset.unsupportedText;

    root.querySelector('[data-market-popup-flag]').className = `country-flags country-flags--${detectedCountry}`;

    root.hidden = false;
    requestAnimationFrame(() => root.classList.add('is-visible'));
    log('show: unsupported-country popup revealed for', detectedCountry);
  }

  function hidePopup() {
    root.classList.remove('is-visible');
    markResolved();
    setTimeout(() => {
      root.hidden = true;
    }, 250); // matches the CSS transition duration
  }

  function init() {
    const resolvedThisSession = isResolvedThisSession();
    log('gate: sessionStorage', DISMISS_KEY, '=', resolvedThisSession ? 'true -> stop' : 'unset -> continue');
    if (resolvedThisSession) return;

    // detectVisitorCountry() runs concurrently with the LCP wait, not after it -- the two
    // don't stack, so this adds no delay beyond whichever of the two takes longer.
    Promise.all([detectVisitorCountry(), lcpSettled]).then(([detected]) => {
      log('gate: detected', detected, '| active', activeCountry);
      if (!detected || detected === activeCountry) {
        log('stop:', !detected ? 'no detected country' : 'detected country equals active country');
        return;
      }

      if (supportedCountries.includes(detected)) {
        showPopup(detected);
      } else {
        showUnsupportedPopup(detected);
      }
    });
  }

  root.querySelectorAll('[data-market-popup-dismiss]').forEach((el) => {
    el.addEventListener('click', hidePopup);
  });

  root.querySelector('select[name="country_code"]')?.addEventListener('change', (event) => {
    syncLocaleFallback(event.currentTarget);
  });

  root.querySelector('form')?.addEventListener('submit', markResolved);

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && root.classList.contains('is-visible')) hidePopup();
  });

  // Debug-only: why is Shopify.customerPrivacy missing? Shopify's loader calls onLoad(error) when the
  // script tag fires 'load' (error arg set on a network failure; an empty 204 body still counts as a
  // successful load but defines nothing). Reads existing resource-timing entries only -- no request,
  // no waiting, and it is never called unless ?mdc_geo is present.
  function logConsentScriptDiagnostics(loadError) {
    let responses = 'n/a';
    try {
      responses =
        performance
          .getEntriesByType('resource')
          .filter((entry) => /consent-tracking-api|storefront-banner/.test(entry.name))
          .map((entry) => entry.name.split('/').pop().split('?')[0] + ' status=' + entry.responseStatus + ' bytes=' + entry.decodedBodySize)
          .join(', ') || 'no resource-timing entry';
    } catch (e) {
      // ignore
    }
    log('consent: onLoad error arg =', loadError ? String(loadError) : 'none', '| responses:', responses);
    log('consent: typeof Shopify.customerPrivacy =', typeof window.Shopify.customerPrivacy, '| typeof Shopify.trackingConsent =', typeof window.Shopify.trackingConsent);
  }

  // Only start detection once the visitor's cookie/privacy choice is resolved (accepted,
  // declined, or not required for their region/market). Kept as-is on purpose even though the
  // lookup is now first-party (decision 2026-10-11: consent gate stays for now). Mirrors the same
  // loadFeatures/onLoad pattern the theme's own privacy-bar section uses (assets/theme.js,
  // js/common/overlay/privacy-bar.js).
  function onConsentResolved(callback) {
    window.Shopify = window.Shopify || {};
    if (typeof window.Shopify.loadFeatures !== 'function') {
      log('consent: Shopify.loadFeatures unavailable -> popup can never run');
      return; // API unavailable -- fail silent
    }

    window.Shopify.loadFeatures([
      {
        name: 'consent-tracking-api',
        version: '0.1',
        onLoad: (loadError) => {
          if (DEBUG) logConsentScriptDiagnostics(loadError);
          if (!window.Shopify.customerPrivacy) {
            log('consent: Shopify.customerPrivacy missing after consent-tracking-api loaded -> popup can never run');
            return;
          }
          const bannerPending = window.Shopify.customerPrivacy.shouldShowBanner();
          if (DEBUG) {
            let visitorConsent = 'unavailable';
            try {
              visitorConsent = JSON.stringify(window.Shopify.customerPrivacy.currentVisitorConsent());
            } catch (e) {
              // ignore
            }
            log('consent: shouldShowBanner =', bannerPending, '| currentVisitorConsent =', visitorConsent);
          }
          if (bannerPending) {
            log('consent: no choice recorded yet -> waiting for visitorConsentCollected');
            document.addEventListener(
              'visitorConsentCollected',
              DEBUG
                ? function () {
                    log('consent: visitorConsentCollected fired -> running init');
                    callback();
                  }
                : callback,
              { once: true }
            );
          } else {
            log('consent: choice already recorded / not required -> running init');
            callback();
          }
        },
      },
    ]);
  }

  onConsentResolved(init);
})();
